import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import {
	BusyIndicator,
	computeContextMenuTitle,
	ContextMenu,
	FloatingActionButton,
	FormattedText,
	Icon,
	Pagination,
	SearchInputForm,
	SearchPlaceholder,
	TableView,
	TableViewNoResultConfig,
	ToolbarAction,
	ToolbarRegularAction,
	useConfirmationDialog,
	useContextMenu
} from "client/Components";
import {
	AlertManager,
	createLocalDataSource,
	MediaQueryKind,
	SelectableItemOptions,
	useAlerts,
	useDeepComparison,
	useForceRerender,
	useSelection,
	useSortHandler,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	deleteApp,
	getPageViewModel,
	IntegrationAppOverviewListItemModel
} from "client/Api/IntegrationBuilder/integrationBuilderApps";
import { contains } from "client/Data/searching";
import {
	DataSourceSortableState,
	DataSourceSortDirection
} from "client/Data/sorting";
import { localize } from "client_legacy/ndaw/culture";
import {
	EMPTY_ARRAY,
	generateTrackingId,
	isNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { integrationBuilderRouting } from "./OverViewPage";
import { useCreateAppDialog } from "./CreateAppDialog";
import { useManageDefaultAppDialog } from "./ManageDefaultAppDialog";
import { NavigationCallback } from "client/Navigation";

type AppsTabContentProps = {
	isActive: boolean;
	onActionsStateChange(
		actions: readonly ToolbarAction<AppActionContext>[],
		context: AppActionContext
	): void;
	onNavigate: NavigationCallback;
};

export const AppsTabContent = (props: AppsTabContentProps): ReactElement => {
	const {
		isActive,
		onActionsStateChange,
		onNavigate
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [apps, setApps] = useState<readonly IntegrationAppOverviewListItemModel[]>(EMPTY_ARRAY);
	const [selectedItems, setSelectedItems] = useState<readonly IntegrationAppOverviewListItemModel[]>(EMPTY_ARRAY);

	const [sortingState, setSortingState] = useState<DataSourceSortableState<IntegrationAppOverviewListItemModel>>({
		propertyName: "name",
		direction: DataSourceSortDirection.Ascending
	});

	const { sortingFor } = useSortHandler<IntegrationAppOverviewListItemModel>(sortingState, setSortingState);
	const itemsModifiersVersion: number = useDeepComparison([searchTerm, sortingState]);
	const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const [onContextMenu, contextMenuProps] = useContextMenu(tableRef);
	const alertManager: AlertManager = useAlerts();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [createAppDialog, openCreateAppDialog] = useCreateAppDialog();
	const [manageDefaultAppDialog, openManageDefaultAppDialog] = useManageDefaultAppDialog();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [reload, loadVersion] = useForceRerender();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const {
		currentItems,
		pagination
	} = useDataSource(
		apps,
		{
			pagination: {
				currentPageNumber
			},
			sorting: sortingState,
			searchTerm
		}
	);

	const {
		multiSelection,
		selectionFor
	} = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems
		}
	);

	const createNewHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<number> = await openCreateAppDialog({});

			if (!result.wasSuccessful) {
				return;
			}

			alertManager.showSuccess(localize("_SuccessfullySavedTheApp"));
			onNavigate(
				integrationBuilderRouting.editApp({
					routeParams: {
						appId: `${result.result}`
					},
					queryParams: {
						tab: "flows"
					}
				})
			);
		},
		[
			alertManager,
			onNavigate,
			openCreateAppDialog
		]
	);

	const editHandler = useCallback(
		(item: IntegrationAppOverviewListItemModel): void => {
			onNavigate(
				integrationBuilderRouting.editApp({
					appId: `${item.id}`
				})
			);
		},
		[onNavigate]
	);

	const deleteHandler = useCallback(
		async (item: IntegrationAppOverviewListItemModel): Promise<void> => {
			const result = await openConfirmationDialog({
				title: localize("_AreYouSureYouWantToDeleteThisApp"),
				children: item.flowCount > 0
					? (
						<FormattedText
							text={localize("_DeletingAnAppAlsoDeletesItsFlowsInfo")}
							thingsToWrap={[
								{
									blockId: "Note",
									wrap: (children) => (
										<b>{children}</b>
									)
								}
							]}
						/>
					)
					: undefined,
				confirmText: localize("_Delete"),
				trackingId: "delete-integration-app-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				await deleteApp(
					item.id,
					globalCancellation
				);

				reload();
				setSelectedItems(EMPTY_ARRAY);
				setIsBusy(false);
				alertManager.showSuccess(localize("_SuccessfullyDeletedTheItems"));
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			openConfirmationDialog,
			reload
		]
	);

	const manageDefaultHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<number | null> = await openManageDefaultAppDialog({});

			if (!result.wasSuccessful) {
				return;
			}

			const defaultAppId: number | null = result.result;

			setApps(
				apps => apps.map(
					app => ({
						...app,
						isDefault: app.id === defaultAppId
					})
				)
			);

			alertManager.showSuccess(
				localize(
					isNil(defaultAppId)
						? "_SuccessfullyRemovedTheDefaultApp"
						: "_SuccessfullySetTheDefaultApp"
				)
			);
		},
		[
			alertManager,
			openManageDefaultAppDialog
		]
	);

	const actionHandlers = useMemo(
		(): ActionHandlers => ({
			edit: editHandler,
			deleteItem: deleteHandler,
			manageDefault: manageDefaultHandler
		}),
		[
			editHandler,
			deleteHandler,
			manageDefaultHandler
		]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<AppActionContext> => getMainAction(createNewHandler),
		[createNewHandler]
	);

	const selectionActions = useMemo(
		(): readonly AppSelectionAction[] => getSelectionActions({
			deselectHandler: (): void => setSelectedItems(EMPTY_ARRAY)
		}),
		[]
	);

	const contextualActions = useMemo(
		(): readonly AppSelectionAction[] => listContextualActions(actionHandlers),
		[actionHandlers]
	);

	const defaultActions = useMemo(
		(): readonly AppSelectionAction[] => listDefaultActions(actionHandlers),
		[actionHandlers]
	);

	const actionContext = useMemo(
		(): AppActionContext => ({
			selection: selectedItems,
			isMobile
		}),
		[
			isMobile,
			selectedItems
		]
	);

	useEffect(
		(): (() => void) | void => {
			if (!isActive) {
				return;
			}

			onActionsStateChange(
				buildActionsForTopBar<AppActionContext, AppSelectionAction>(
					{
						contextualActions,
						selectionActions,
						defaultActions,
						mainActions: [mainAction]
					},
					actionContext.selection.length !== 0,
					isResponsive,
					false
				),
				actionContext
			);

			return (): void => {
				onActionsStateChange(
					[],
					{
						selection: [],
						isMobile: false
					}
				);
			};
		},
		[
			mainAction,
			selectionActions,
			contextualActions,
			defaultActions,
			isResponsive,
			isActive,
			actionContext,
			onActionsStateChange
		]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(itemsModifiersVersion);
			setCurrentPageNumber(0);
		},
		[itemsModifiersVersion]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(itemsModifiersVersion, currentPageNumber);
			tableRef.current
				?.scrollTo(0, 0);
		},
		[itemsModifiersVersion, currentPageNumber]
	);

	useEffect(
		(): (() => void) => {
			hasImplicitDependencyOn(loadVersion);

			async function fetchItems(signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const { apps } = await getPageViewModel(signal)
						.catch(handlePageDataLoadingError);

					setApps(apps);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			const cancellation = new AbortController();
			fetchItems(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			loadVersion
		]
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig | undefined => {
			const isEmpty: boolean = !isBusy
				&& currentItems.length === 0
				&& currentPageNumber === 0;

			if (!isEmpty) {
				return undefined;
			}

			if (searchTerm) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "gear",
				title: localize("_NoAppsYet"),
				message: localize("_ThereAreNoAppsYet")
			};
		},
		[
			currentPageNumber,
			isBusy,
			searchTerm,
			currentItems.length
		]
	);

	return (
		<>
			{isBusy && <BusyIndicator />}
			<SettingsPage.Section height="stretch">
				<SettingsPage.SearchSection>
					<SearchInputForm
						onChange={setSearchTerm}
						trackingId="search-term"
						icon="filter"
						height="large"
						placeholder={(
							<SearchPlaceholder
								kind="filter"
								properties={[localize("_Name")]}
							/>
						)}
					/>
				</SettingsPage.SearchSection>
				<SettingsPage.Section height="stretch">
					<TableView
						multiSelectable={multiSelection}
						trackingId="integration-apps-table"
						items={currentItems}
						columns={[
							{
								title: localize("_Name"),
								trackingId: "name",
								width: "fill",
								sortable: sortingFor("name")
							},
							{
								title: localize("_Flows"),
								trackingId: "flows",
								width: "fill",
								sortable: sortingFor("flowCount")
							},
							{
								title: localize("_Default"),
								trackingId: "is-default",
								width: "auto",
								sortable: sortingFor("isDefault")
							}
						]}
						ref={tableRef}
						height="stretch"
						itemKey="id"
						noResultConfig={noResultConfig}
					>
						{item => {
							const selectableOptions: SelectableItemOptions = selectionFor(item);

							return (
								<TableView.Row
									selectable={selectableOptions}
									trackingId={`${item.id}`}
									onContextMenu={onContextMenu}
									onExecute={() => editHandler(item)}
								>
									<TableView.Cell tooltip={item.name}>
										{item.name}
									</TableView.Cell>
									<TableView.Cell>{item.flowCount}</TableView.Cell>
									<TableView.Cell>
										{item.isDefault && (
											<Icon value="check" size="large" />
										)}
									</TableView.Cell>
								</TableView.Row>
							);
						}}
					</TableView>
					<Pagination
						{...pagination}
						onPageNumberChanged={setCurrentPageNumber}
					/>
					{contextMenuProps && (
						<ContextMenu
							{...contextMenuProps}
							actions={contextualActions}
							context={actionContext}
							trackingId={generateTrackingId("integration-apps-overview-context-menu")}
							title={computeContextMenuTitle(
								actionContext.selection,
								(x) => x.name
							)}
						/>
					)}
				</SettingsPage.Section>
			</SettingsPage.Section>
			<div className="preserve-space-for-mobile-plus-button"/>
			{isActive && mainAction.canExecute(actionContext) && (
				<FloatingActionButton
					icon="plus"
					onClick={() => mainAction.execute(actionContext)}
					trackingId="integration-apps-create-new-fab"
				/>
			)}

			{confirmationDialog}
			{createAppDialog}
			{manageDefaultAppDialog}
		</>
	);
};
AppsTabContent.displayName = "AppsTabContent";

export type AppActionContext = {
	selection: readonly IntegrationAppOverviewListItemModel[];
	isMobile: boolean;
};

export type AppSelectionAction = ToolbarAction<AppActionContext>;

type ActionHandlers = {
	edit: (item: IntegrationAppOverviewListItemModel) => void;
	deleteItem: (item: IntegrationAppOverviewListItemModel) => void;
	manageDefault: () => void;
};

function listContextualActions(
	handlers: ActionHandlers
): readonly AppSelectionAction[] {
	const {
		edit,
		deleteItem
	} = handlers;

	return [
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			canExecute: (c) => c.selection
				.length === 1,
			execute: (c) => edit(c.selection[0]),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			canExecute: (c) => c.selection
				.length === 1,
			execute: (c) => deleteItem(c.selection[0]),
			icon: "trash-can"
		}
	];
}

function listDefaultActions(
	handlers: ActionHandlers
): readonly AppSelectionAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "manage-default-app",
			title: localize("_ManageDefaultApp"),
			canExecute: () => true,
			execute: () => handlers.manageDefault(),
			icon: "star"
		}
	];
}

function getMainAction(
	createNew: () => void
): ToolbarRegularAction<AppActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "new",
		title: localize("_New"),
		canExecute: () => true,
		execute: (_) => createNew(),
		icon: "plus",
		isPrimary: true
	};
}

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(
	handlers: SelectionActionsHandlers
): readonly AppSelectionAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selection),
			icon: "circle-xmark",
			canExecute: c => c
				.selection
				.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selection.length > 0
		}
	];
}

const useDataSource = createLocalDataSource<IntegrationAppOverviewListItemModel>()
	.setConfiguration({
		search: {
			doesMatchItem: searchMatchFunction
		},
		hasPagination: true
	})
	.getHook();

function searchMatchFunction(
	item: IntegrationAppOverviewListItemModel,
	normalizedNotEmptySearchTerm: Exclude<string, "">
): boolean {
	return contains(item.name, normalizedNotEmptySearchTerm);
}
